import { createStore } from "vuex";
import { apiClient } from "@/api/api";
import { jwtDecode } from "jwt-decode";
import { Role } from "@/_helpers/role";
import { get } from "http";

function isTokenExpired(token: string) {
  if (!token) return true;
  const decoded = jwtDecode<{ exp: number }>(token);
  const exp = decoded.exp;
  return exp * 1000 < Date.now();
}

interface CustomJwtPayload {
  exp: number;
  "http://schemas.microsoft.com/ws/2008/06/identity/claims/role": string;
}
interface ConfiguracionOS {
  nombre: string;
  requiereTokenRealizacion: boolean;
  aceptaTokenCero: boolean;
}

function decodeJwt(token: string): CustomJwtPayload {
  const decoded = jwtDecode<CustomJwtPayload>(token);
  return decoded;
}

export default createStore({
  state: () => {
    return {
      isLogged: localStorage.getItem('isLogged') === 'true' || false,
      facturadorSeleccionado: JSON.parse(localStorage.getItem('facturador') || "{}"),
      modoSeleccionado: localStorage.getItem('nivel') || "",
      modosPredefinidos: [
        { nombre: "Primer Nivel", vista: "elegibilidad" },
        { nombre: "Segundo Nivel", vista: "" },
      ],
      redireccionPorRol: [
        { rol: Role.AdministrativoFacturador, redireccion: "/cargar-prestacion/elegibilidad" },
        { rol: Role.AdministrativoReintegros, redireccion: "/auditoria-reintegros" },
        { rol: Role.Auditor, redireccion: "/auditoria/eleccion-os" },
        { rol: Role.Estadista, redireccion: "/estadisticas" },
        { rol: Role.FacturadorIOMA, redireccion: "/cargar-prestacion/elegibilidad" },
        { rol: Role.Administrador, redireccion: "/administrador" },
        { rol: Role.Autorizador, redireccion: "/listado-solicitudes-autorizador" },
        { rol: Role.AuditorGeneral, redireccion: "/listado-solicitudes" },
      ],
      paciente: JSON.parse(localStorage.getItem('paciente') || "{}"),
      username: localStorage.getItem('username'),
      jwt: localStorage.getItem('jwt'),
      profesional: JSON.parse(localStorage.getItem('profesional') || "{}"),
      unicoNivEst: Boolean(localStorage.getItem('unicoNivEst')=='true') || Boolean(false),
      ingreso: JSON.parse(localStorage.getItem('ingreso') || "{}"),
      roles: [] as string[],//por ahora no lo uso
      role: localStorage.getItem('role') || "",
      pedirDatosPaciente: Boolean(localStorage.getItem('pedirDatosPaciente') === 'true') || Boolean(false),
      obraSocial: JSON.parse(localStorage.getItem('obraSocial') || "{}"),
      planAfiliatorio: localStorage.getItem('planAfiliatorio'),
      configuracionesOS: JSON.parse(localStorage.getItem('configuracionesOS') || "{}"),
      profesionalUsuario: JSON.parse(localStorage.getItem('profesionalUsuario') || "{}"),
      idProfesion: localStorage.getItem('idProfesion'),
      configuracionOS: {
        nombre: "",
        requiereTokenRealizacion: false,
        aceptaTokenCero: false,
      },
      configuracionesRealizacion: [] as ConfiguracionOS[],
    };
  },
  getters: {
    isAuthenticated: (state) => state.isLogged,
    getUsername:(state) => state.username,
    getModos: (state) => state.modosPredefinidos,
    getVista(state) {
      const modo = state.modosPredefinidos.find(
        (modo) => modo.nombre === state.modoSeleccionado
      );
      return modo ? modo.vista : null;
    },
    getRedireccion(state){
      const redireccion = state.redireccionPorRol.find(
        (redireccion) => redireccion.rol === state.role
      );
      return redireccion ? redireccion.redireccion : null;
    },
    getPaciente: (state) => state.paciente,
    getFacturador: (state) => state.facturadorSeleccionado,
    getNivel: (state) => state.modoSeleccionado,
    getToken:(state) => state.jwt,
    getProfesional:(state) => state.profesional,
    getUnicoNivEst:(state) => state.unicoNivEst,
    getIngreso:(state) => state.ingreso,
    getRoles:(state) =>{//por ahora no lo uso
      const storedRoles = localStorage.getItem('roles');
      if(storedRoles){
        state.roles = JSON.parse(storedRoles);
      }
      else{
        state.roles = [];
      }
      return state.roles;
    },
    getRole:(state) => state.role,
    getPedirDatosPaciente:(state) => state.pedirDatosPaciente,
    getObraSocial:(state) => state.obraSocial,
    getPlanAfiliatorio:(state) => state.planAfiliatorio,
    getConfiguracionesOS:(state) => state.configuracionesOS,
    getProfesionalUsuario:(state) => state.profesionalUsuario,
    getJwt:(state) => state.jwt,
    getIdProfesion:(state) => state.idProfesion,
    getConfiguracionesRealizacion:(state) => (obraSocial: string) => {
      return state.configuracionesRealizacion.find(
        config => config.nombre === obraSocial
      );
    }
  },
  actions: {
    async login({ commit }, { email, password }) {
      const endpoint = "Auth/Login";
      const body = JSON.stringify({
        username: email,
        password: password,
      });
      try {
        const response = await apiClient.post(endpoint, body);
        if (response.status == 200) {
          commit("setIsLogged", true);
          commit("setUsername",email);
          commit("setProfesionalUsuario",response.data.profesional);
          const token = response.data.token;
          const decoded = decodeJwt(token);
          commit("setRole",decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"].toString());
          localStorage.setItem("jwt", token);
        }
        return response.data;
      } catch (error: unknown) {
        if (error instanceof Error) {
          console.error(error.message);
          return "invalid";
        } else {
          console.error("Unknown error occurred");
          return "invalid";
        }
      }
    },
    checkTokenExpiration({ state, dispatch }) {
      if ( state.jwt && isTokenExpired(state.jwt)) {
        dispatch('logout');
      }
    },
  },
  mutations: {
    logout(state, redirect:boolean) {
      state.facturadorSeleccionado = {};
      state.modoSeleccionado = "";
      state.paciente = {};
      state.profesional = {};
      state.username = "";
      state.isLogged = false;
      state.jwt = "";
      state.roles=[] as string[];
      state.role = "";
      state.pedirDatosPaciente = false;
      state.obraSocial = {};
      state.planAfiliatorio = "";
      state.configuracionesOS = {};
      localStorage.removeItem("jwt");
      localStorage.removeItem("user");
      localStorage.removeItem("paciente");
      localStorage.removeItem("isLogged");
      localStorage.removeItem("facturador");
      localStorage.removeItem("nivel");
      localStorage.removeItem("username");
      localStorage.removeItem("profesional");
      localStorage.removeItem("unicoNivEst");
      localStorage.removeItem("ingreso");
      localStorage.removeItem("roles");//por ahora no lo uso
      localStorage.removeItem("role");
      localStorage.removeItem("pedirDatosPaciente");
      localStorage.removeItem("obraSocial");
      localStorage.removeItem("planAfiliatorio");
      localStorage.removeItem("configuracionesOS");
      localStorage.removeItem("profesionalUsuario");
      localStorage.removeItem("idProfesion");
      if(redirect){
        window.location.href = "/";
      }
    },
    setFacturador(state, Facturador) {
      state.facturadorSeleccionado = Facturador;
      localStorage.setItem('facturador',JSON.stringify(Facturador));
    },
    setModoSeleccionado(state, Modo) {
      state.modoSeleccionado = Modo;
      localStorage.setItem('nivel',Modo);
    },
    setIsLogged(state, status) {
      state.isLogged = status;
      localStorage.setItem('isLogged', status);
    },
    setUsername(state, username){
      state.username = username
      localStorage.setItem('username',username);
    },
    setPaciente(state, Paciente) {
      state.paciente = Paciente;
      localStorage.setItem("paciente", JSON.stringify(Paciente));
    },
    setProfesional(state, Profesional) {
      state.profesional = Profesional;
      localStorage.setItem("profesional", JSON.stringify(Profesional));
    },
    removeProfesional(state) {
      state.profesional = {};
      localStorage.removeItem("profesional");
    },
    setUnicoNivEst(state,unicoNivEst){
      state.unicoNivEst = unicoNivEst;
      localStorage.setItem('unicoNivEst',unicoNivEst);
    },
    setIngreso(state,Ingreso){
      state.ingreso = Ingreso;
      localStorage.setItem("ingreso", JSON.stringify(Ingreso));
    },
    addRole(state,role){ //por ahora no lo uso
      if(!state.roles.includes(role)){
        state.roles.push(role);
        localStorage.setItem('roles', JSON.stringify(state.roles));
      }
    },
    setRole(state,role){
      state.role = role;
      localStorage.setItem('role',role);
    },
    setPedirDatosPaciente(state,pedirDatosPaciente){
      state.pedirDatosPaciente = pedirDatosPaciente;
      localStorage.setItem('pedirDatosPaciente',pedirDatosPaciente);
    },
    setObraSocial(state,obraSocial){
      state.obraSocial = obraSocial;
      localStorage.setItem('obraSocial',JSON.stringify(obraSocial));
    },
    setPlanAfiliatorio(state,planAfiliatorio){
      state.planAfiliatorio = planAfiliatorio;
      localStorage.setItem('planAfiliatorio', planAfiliatorio);
    },
    setConfiguracionesOS(state,configuracionesOS){
      state.configuracionesOS = configuracionesOS;
      localStorage.setItem('configuracionesOS',JSON.stringify(configuracionesOS));
    },
    setProfesionalUsuario(state,profesionalUsuario){
      state.profesionalUsuario = profesionalUsuario;
      localStorage.setItem('profesionalUsuario',JSON.stringify(profesionalUsuario));
    },
    setIdProfesion(state,idProfesion){
      state.idProfesion = idProfesion;
      localStorage.setItem('idProfesion',idProfesion);
    },
    setConfiguraciones(state, nuevasConfiguraciones: ConfiguracionOS[]) {
      state.configuracionesRealizacion = nuevasConfiguraciones;
    }
  },
  modules: {},
});
